import { ref } from 'vue'
import type { Ref, UnwrapRef } from 'vue'

import { useLatestRequest } from '~/composables/useLatestRequest'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import { useApiClient } from '~/services/api'
import { AppProblemModel } from '~/services/api/AppProblemModel'
import { resolveProblemMessage } from '~/services/api/resolveProblemMessage'
import type { Brand } from '~/services/api/interfaces/Brand'

/** The public brand page's brand read: in flight, loaded, or failed with a localized message. */
type PublicBrandLoad =
  | { readonly status: 'loading' }
  | { readonly status: 'loaded'; readonly brand: Brand }
  | { readonly status: 'failed'; readonly message: string }

interface UsePublicBrandReturn {
  brandLoad: Ref<UnwrapRef<PublicBrandLoad>>
  fetchBrand: () => Promise<void>
}

/** The public brand page's brand, read by slug, as a loading, loaded, or failed state. */
export function usePublicBrand(slug: () => string): UsePublicBrandReturn {
  const { brand: brandApi } = useApiClient()
  const t = usePresentationTranslator()

  const latest = useLatestRequest()

  const brandLoad = ref<PublicBrandLoad>({ status: 'loading' })

  const brandNotFound = (): PublicBrandLoad => ({
    get message() {
      return t('commerce.brandSearchCard.brandNotFound')
    },
    status: 'failed'
  })

  const fetchBrand = async () => {
    const ticket = latest.begin()
    brandLoad.value = { status: 'loading' }

    try {
      const { data } = await brandApi().publicShow(slug())
      if (!latest.isLatest(ticket)) return
      brandLoad.value = data === null ? brandNotFound() : { brand: data, status: 'loaded' }
    } catch (caught) {
      if (!latest.isLatest(ticket)) return
      const problem = AppProblemModel.normalize(caught, { context: 'brand page' })
      brandLoad.value = {
        get message() {
          return resolveProblemMessage(problem, t, t('commerce.brandSearchCard.brandNotFound'))
        },
        status: 'failed'
      }
    }
  }

  return { brandLoad, fetchBrand }
}
